/**
 * Alerts bell — the ONLY alerts surface. No standalone page.
 *
 *   • Badge count  → GET /summary (unacknowledged), polled ~45s.
 *   • Dropdown     → GET recent alerts (both states) while open.
 *   • Two tabs     → Unacknowledged (default) / Acknowledged.
 *   • Click a row  → acknowledge it (POST /{id}/acknowledge). It moves to the
 *                    Acknowledged tab; the row shows a green tick + who/when.
 *   • "Ack all"    → bulk-acknowledge every unacked row (POST /acknowledge).
 *
 * Self-contained so the Header stays chrome-only — AppShell injects this.
 */
import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "@features/auth/AuthContext";
import { useCan } from "@features/auth/hooks/useCan";
import { useAlertSummary, useAlerts, useAcknowledge, useAcknowledgeBulk } from "../hooks/useResourceAlerts";
import { ALERT_TYPE_ICON, timeAgo, fmtValue, actorName } from "../utils";
import styles from "./AlertsBell.module.scss";

type Tab = "unacked" | "acked";

export const AlertsBell: React.FC = () => {
  const { user } = useAuth();
  const { can } = useCan();
  const orgId = user?.org_id ?? "";
  const canAck = can("alerts:acknowledge");

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>("unacked");
  const [ackingIds, setAckingIds] = useState<Set<string>>(new Set());
  const [ackingAll, setAckingAll] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const summaryQ = useAlertSummary(orgId);
  // Fetch recent alerts in BOTH states while open; we split them into tabs.
  const listQ = useAlerts(orgId, { per_page: 50 }, {}, open);
  const ackMut = useAcknowledge(orgId);
  const ackBulkMut = useAcknowledgeBulk(orgId);

  const unacked = summaryQ.data?.unacknowledged ?? 0;
  const rows = listQ.data?.items ?? [];
  const unackedRows = rows.filter((r) => !r.acknowledged);
  const ackedRows = rows.filter((r) => r.acknowledged);
  const shown = tab === "unacked" ? unackedRows : ackedRows;

  // Whenever the dropdown re-opens, start on the Unacknowledged tab.
  useEffect(() => { if (open) setTab("unacked"); }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const ackOne = async (id: string): Promise<void> => {
    if (!canAck || ackingIds.has(id)) return;
    setAckingIds((s) => new Set(s).add(id));
    try { await ackMut.mutateAsync(id); }
    catch { /* count refreshes on next poll */ }
    finally { setAckingIds((s) => { const n = new Set(s); n.delete(id); return n; }); }
  };

  const ackAll = async (): Promise<void> => {
    if (!canAck || unackedRows.length === 0 || ackingAll) return;
    setAckingAll(true);
    try { await ackBulkMut.mutateAsync(unackedRows.map((r) => r.alert_id)); }
    catch { /* count refreshes on next poll */ }
    finally { setAckingAll(false); }
  };

  const ackAllDisabled = ackingAll || unackedRows.length === 0;

  return (
    <div className={styles.wrap} ref={rootRef}>
      <button
        type="button"
        className={`${styles.bell} ${unacked > 0 ? styles.active : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${unacked} unacknowledged alerts`}
        title="Alerts"
      >
        <i className="fas fa-bell" />
        {unacked > 0 && <span className={styles.count}>{unacked > 99 ? "99+" : unacked}</span>}
      </button>

      {open && (
        <div className={styles.drop} role="menu">
          <div className={styles.head}>
            <span className={styles.headTitle}>Alerts</span>
            {canAck && tab === "unacked" && (
              <button
                type="button"
                className={styles.ackAll}
                onClick={() => { void ackAll(); }}
                disabled={ackAllDisabled}
                title={unackedRows.length === 0 ? "Nothing to acknowledge" : "Acknowledge all"}
              >
                {ackingAll
                  ? <><span className={styles.spin} /> Acknowledging…</>
                  : <><i className="fas fa-check-double" /> Ack all{unackedRows.length > 0 ? ` (${unackedRows.length})` : ""}</>}
              </button>
            )}
          </div>

          {/* Tab toggle — Unacknowledged (default) / Acknowledged */}
          <div className={styles.tabs} role="tablist">
            <button
              type="button" role="tab" aria-selected={tab === "unacked"}
              className={`${styles.tab} ${tab === "unacked" ? styles.tabOn : ""}`}
              onClick={() => setTab("unacked")}
            >
              Unacknowledged
              <span className={styles.tabCount}>{unackedRows.length}</span>
            </button>
            <button
              type="button" role="tab" aria-selected={tab === "acked"}
              className={`${styles.tab} ${tab === "acked" ? styles.tabOn : ""}`}
              onClick={() => setTab("acked")}
            >
              Acknowledged
              <span className={styles.tabCount}>{ackedRows.length}</span>
            </button>
          </div>

          <div className={styles.body}>
            {listQ.isLoading ? (
              <div className={styles.msg}><span className={styles.spin} /> Loading…</div>
            ) : shown.length === 0 ? (
              <div className={styles.empty}>
                <i className="fas fa-circle-check" />
                <div>{tab === "unacked" ? "You're all caught up" : "Nothing acknowledged yet"}</div>
              </div>
            ) : (
              shown.map((a) => {
                const busy = ackingIds.has(a.alert_id) || ackingAll;
                const acked = a.acknowledged;
                return (
                  <button
                    key={a.alert_id}
                    type="button"
                    className={`${styles.row} ${acked ? styles.acked : ""}`}
                    onClick={() => { if (!acked) void ackOne(a.alert_id); }}
                    disabled={busy || !canAck || acked}
                    role="menuitem"
                    title={acked ? "Acknowledged" : canAck ? "Click to acknowledge" : undefined}
                  >
                    <span className={`${styles.sevDot} ${styles[a.severity] ?? ""}`} />
                    <span className={styles.rowMain}>
                      <span className={styles.rowTitle}>
                        <i className={`fas ${ALERT_TYPE_ICON[a.alert_type]}`} /> {a.title}
                      </span>
                      <span className={styles.rowSub}>
                        {acked
                          ? <>Acknowledged{a.acknowledged_by ? ` by ${actorName(a.acknowledged_by)}` : ""} · {timeAgo(a.acknowledged_at)}</>
                          : <>{a.host ?? "—"} · {timeAgo(a.last_ts)}</>}
                      </span>
                    </span>
                    {!acked && <span className={styles.rowVal}>{fmtValue(a.last_value ?? a.metric?.value, a.metric?.unit)}</span>}
                    <span className={`${styles.rowAck} ${acked ? styles.rowAckDone : ""}`} aria-hidden="true">
                      <i className={`fas ${busy ? "fa-spinner fa-spin" : acked ? "fa-circle-check" : "fa-check"}`} />
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};
